import { supabase } from "@/lib/supabase";
import { User, Session } from "@supabase/supabase-js";
import { UserRole } from "@/lib/auth-utils";
import { Loading } from "@/components/ui/loading";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef
} from "react";
import { UserProfile, getProfileTableFromUserRole } from "@/lib/auth-utils";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface SignUpOptions {
  data?: {
    [key: string]: any;
  };
  captchaToken?: string;
}

interface GetProfileOptions {
  createIfMissing: boolean;
  userRole: string;
  email: string;
  companyId: string | null;
  partnerId: string | null;
}

interface ImpersonationState {
  impersonatedUser: User;
  profile: UserProfile;
  originalProfile: UserProfile;
}

interface ProfileInsertData {
  id: string;
  email: string;
  partner_id?: string;
  company_id?: string;
}

interface TargetUserData {
  id: string;
  email?: string;
  role: UserRole;
  given_name?: string;
  family_name?: string;
  company_id?: string;
}

interface AuthContextType {
  profile: UserProfile;
  loading: boolean;
  originalProfile: UserProfile | null;
  impersonatedUser: User | null;
  impersonateUser: (userId: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    options?: SignUpOptions
  ) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultProfile: UserProfile = {
  id: "",
  email: "",
  role: "talent",
  isSuperUser: false,
  anonymous: true
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [realUser, setRealUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);
  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation("common");
  // Store real user ref to avoid calling set state when it does not really change.
  const realUserRef = useRef(realUser);
  useEffect(() => {
    realUserRef.current = realUser;
  }, [realUser]);

  // UUID validation function
  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Load impersonation state from localStorage on startup
  useEffect(() => {
    const savedImpersonationState = localStorage.getItem("impersonation_state");
    if (savedImpersonationState) {
      try {
        const state: ImpersonationState = JSON.parse(savedImpersonationState);
        setImpersonatedUser(state.impersonatedUser);
        setProfile(state.profile);
        setOriginalProfile(state.originalProfile);
        setLoading(false); // Set loading to false since we have all the data we need
      } catch (error) {
        console.error("Error loading impersonation state:", error);
        localStorage.removeItem("impersonation_state");
      }
    }
  }, []);

  useEffect(() => {
    const getNewSession = (session: Session | null) => session?.user ?? null;
    const shouldUpdateSession = (
      newSession: User | null,
      currentSession: User | null
    ) => JSON.stringify(newSession) !== JSON.stringify(currentSession);
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentSession = realUserRef.current;
      const newSession = getNewSession(session);
      if (shouldUpdateSession(newSession, currentSession)) {
        setRealUser(newSession);
      }
      // Don't set loading to false yet - wait for profile to load
      if (!session?.user) {
        setLoading(false); // No user, so we're done loading
      }
    });
    // Listen for changes on auth state (logged in, signed out, etc.)
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentSession = realUserRef.current;
      const newSession = getNewSession(session);
      if (shouldUpdateSession(newSession, currentSession)) {
        setRealUser(newSession);
      }
      // Don't set loading to false yet - wait for profile to load
      if (!session?.user) {
        setLoading(false); // No user, so we're done loading
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Get the user profile from supabase
    const getProfile = async (userId: string, options: GetProfileOptions) => {
      if (!options.userRole) throw new Error("Role must be defined");
      const role = options.userRole as UserRole;

      const tableName = getProfileTableFromUserRole(role);
      if (!tableName)
        throw new Error(`Unrecognized profile table for role ${role}`);
      const { data, error } = await supabase
        .from(tableName)
        .select("*")
        .eq("id", userId)
        .single();

      // This condition should only happen after the first user's login.
      if (error && error.code === "PGRST116" && options.createIfMissing) {
        const userData: ProfileInsertData = {
          id: userId,
          email: options.email || ""
        };
        // CompanyId and PartnerId are present if the user is being invited by a company or partner.
        if ((role === "partner" || role === "talent") && options.partnerId) {
          userData.partner_id = options.partnerId!;
        } else if (role === "admin" && options.companyId) {
          userData.company_id = options.companyId;
        }
        const { data: insertData, error: insertError } = await supabase
          .from(tableName)
          .insert(userData)
          .select()
          .single();
        if (insertError) {
          console.error("Profile creation error:", insertError);
          toast({
            title: t("errors.error"),
            description: t("errors.profile_creation_failed"),
            variant: "destructive"
          });
          throw insertError;
        }
        return insertData;
      }

      if (error) {
        console.error("Profile retrieval error:", error);
        toast({
          title: t("errors.error"),
          description: t("errors.profile_loading_failed"),
          variant: "destructive"
        });
        throw error;
      }
      return data;
    };

    const loadProfileAsync = async (
      signupRole: string,
      isSuperUser: boolean,
      companyId: string | null,
      partnerId: string | null
    ) => {
      try {
        // Validate UUID format for companyId and partnerId if they are defined
        if (companyId && !isValidUUID(companyId)) {
          toast({
            title: t("errors.error"),
            description: t("errors.invalid_company_id"),
            variant: "destructive"
          });
          throw new Error(`Invalid company ID format: ${companyId}`);
        }

        if (partnerId && !isValidUUID(partnerId)) {
          toast({
            title: t("errors.error"),
            description: t("errors.invalid_partner_id"),
            variant: "destructive"
          });
          throw new Error(`Invalid partner ID format: ${partnerId}`);
        }

        const userProfile = await getProfile(realUser!.id, {
          createIfMissing: true,
          // Note this is the role the user has signed up with, meaning
          // talent, partner or admin, but not superuser.
          userRole: signupRole,
          companyId,
          partnerId,
          email: realUser!.email || ""
        });
        const updatedProfile = {
          ...userProfile,
          role: signupRole,
          isSuperUser,
          email: realUser!.email || "",
          anonymous: false
        };
        setProfile(updatedProfile);
        setLoading(false); // Now we're done loading
      } finally {
        setLoading(false);
      }
    };
    if (realUser && !impersonatedUser) {
      const signupRole = realUser!.app_metadata.roles.filter(
        (role: string) => role !== "superuser"
      )[0];
      const isSuperUser = realUser!.app_metadata.roles.includes("superuser");
      loadProfileAsync(
        signupRole,
        isSuperUser,
        realUser.user_metadata.company_id,
        realUser.user_metadata.partner_id
      );
    }
  }, [realUser, impersonatedUser, t, toast]);

  const signUp = async (
    email: string,
    password: string,
    options: SignUpOptions = {}
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear impersonation state when signing out
    setProfile(defaultProfile);
    setOriginalProfile(null);
    setImpersonatedUser(null);
    localStorage.removeItem("impersonation_state");
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
  };

  const impersonateUser = async (userId: string) => {
    if (!profile || !profile.isSuperUser) {
      throw new Error("Only superusers can impersonate other users");
    }
    // Store the original profile
    setOriginalProfile(profile);
    // Get the target user's profile and email using admin function
    const { data: targetUserData, error } = await supabase.rpc(
      "get_all_users_admin_2"
    );
    if (error) throw error;

    const targetUser = targetUserData?.find(
      (u: TargetUserData) => u.id === userId
    );
    if (!targetUser) throw new Error("Target user not found");
    // Create a fake user object for the impersonated user
    const fakeUser: User = {
      id: targetUser.id,
      email: targetUser.email || "",
      aud: realUser?.aud || "",
      app_metadata: { roles: [targetUser.user_type] },
      user_metadata: {},
      created_at: realUser?.created_at || new Date().toISOString(),
      updated_at: realUser?.updated_at || new Date().toISOString()
    };
    // Set the impersonated user and profile
    const impersonatedProfile = {
      id: targetUser.id,
      role: targetUser.user_type,
      given_name: targetUser.given_name,
      family_name: targetUser.family_name,
      email: targetUser.email,
      company_id: targetUser.company_id,
      partner_id: targetUser.partner_id,
      // Does not inherit the superuser status
      isSuperUser: false,
      anonymous: false
    };
    setImpersonatedUser(fakeUser);
    setProfile(impersonatedProfile);
    // Save impersonation state to localStorage
    localStorage.setItem(
      "impersonation_state",
      JSON.stringify({
        impersonatedUser: fakeUser,
        profile: impersonatedProfile,
        originalProfile: profile
      })
    );
  };

  const exitImpersonation = () => {
    if (!impersonatedUser || !originalProfile) return;

    setProfile(originalProfile);
    setOriginalProfile(null);
    setImpersonatedUser(null);

    // Remove impersonation state from localStorage
    localStorage.removeItem("impersonation_state");
  };
  if (loading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider
      value={{
        profile,
        originalProfile,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        impersonatedUser,
        impersonateUser,
        exitImpersonation
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
